/**
 * Scroll to and focus the first invalid field after validation or API errors.
 * Use with nextTick() after setting fieldErrors so the DOM has updated.
 * If the first error field is not in the DOM (e.g. another step), tries the next error in order.
 *
 * @param {Record<string, string>} fieldErrors - Object of field name -> error message (only keys with truthy values count)
 * @param {string[]} fieldOrder - Ordered list of field names; first key in this order that has an error is used
 * @param {Record<string, string>} [selectorMap] - Optional map field name -> CSS selector or #id for the field container or input
 * @returns {void}
 */
export function scrollToFirstInvalidField(fieldErrors, fieldOrder, selectorMap = {}) {
  if (!fieldErrors || typeof fieldErrors !== 'object' || !Array.isArray(fieldOrder) || fieldOrder.length === 0) return
  for (const key of fieldOrder) {
    if (!fieldErrors[key]) continue
    const selector = selectorMap[key] ?? `[data-error-field="${key}"]`
    const el = typeof document !== 'undefined' ? document.querySelector(selector) : null
    if (!el) continue
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // Defer focus so smooth scroll can complete and we don't compete with submit-button focus
    const doFocus = () => {
      const focusable =
        el.querySelector?.('input, textarea, select, [contenteditable="true"]') ??
        (el.matches?.('input, textarea, select') ? el : null)
      if (focusable && typeof focusable.focus === 'function') focusable.focus()
      else if (typeof el.focus === 'function') el.focus()
    }
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTimeout(doFocus, 300))
      })
    } else {
      setTimeout(doFocus, 300)
    }
    return
  }
}
